import React from 'react';
import Head from 'next/head';
import { useProductCard } from '@/components/categ/CategCU/useProductCard';
import { useTruncateIfOverflow } from '@/hooks/useTruncateIfOverflow';
import { formatUrl, getHrefForCategLink } from '@/utils/linkUtils';

import ProductImage from '@/components/product/ProductImage';
import ProductCardName from '@/components/listing/ProductCardName';
import Dropdown from '@/components/common/design-system/Dropdown.ds';
import WishListIcon from '@/components/account/wishList/WishListIcon';
import CallToActionContainer from '@/components/common/callToAction/CallToActionContainer';
import AvailabilityIndicator from '@/components/common/AvailabilityIndicator';
import MillesimaLink from '@/components/common/MillesimaLink';
import ProductCardAttributes from '@/components/listing/ProductCardAttributes';
import ProductRatingList from '@/components/common/product/rating/ProductRatingList';
import CategViewLabelBio from '@/components/categ/CategViewLabelBio';
import ProductPrice from '@/components/product/ProductPrice';

import stylesGrid from './ProductCardCU.module.scss';
import stylesLine from './ProductCard.list.module.scss';
import { useScreenSizeContext } from '@/components/ScreenSize.context';
import MillesimaPrice from '@/components/common/price/MillesimaPrice';
import { isProductTtc } from '@/utils/productUtils';

export const ProductCard = ({
  product,
  index,
  item,
  view = 'grid',
  showUnitBloc = true,
  visibilityTracking,
  isCategCu = false
}) => {
  const presenter = useProductCard(product, item, visibilityTracking);
  const { ref, truncated } = useTruncateIfOverflow();
  const styles = view === 'grid' ? stylesGrid : stylesLine;
  const isMobile = useScreenSizeContext().isMobile;
  const isTtc = isProductTtc(product, presenter.currentItem);
  const isCUItem = presenter.currentItem.partnumber.includes('CU');

  const catchPhraseStyle = [
    styles['catch-phrase'],
    truncated && styles.truncated
  ].filter(Boolean).join(' ');

  const actionsStyle = [
    styles.actions,
    isCategCu && styles['no-gap']
  ].filter(Boolean).join(' ');

  return (
    <>
      {index <= 3 && (
        <Head>
          <link rel="preload" href={presenter.url} as="image" />
        </Head>
      )}
      <div className={styles.container} ref={presenter.productCardRef}>
        <WishListIcon
          className={styles['wishlist']}
          product={{
            ...product,
            partnumber: presenter.currentItem.partnumber.split('/').slice(0, 2).join('/'),
          }}
        />

        <ProductImage
          onClick={presenter.handleClickProduct}
          customClassName={styles['product-img']}
          url={presenter.url}
          alt={presenter.productName}
          position={index}
        />
        <div className={styles['ratings-bio-label']}>
          <ProductRatingList
            ratings={presenter.ratings}
            ratingMaxNumber={presenter.isMobile ? 1 : 3}
            onClick={presenter.handleClickProduct}
          />

          <CategViewLabelBio
            t={presenter.t}
            environnement={product.attributes.environnement}
            isMobileView={presenter.isMobile}
            isSpirit={product.isSpirit}
            storeConf={presenter.storeConf}
            productPosition={0}
          />
        </div>
        <div className={styles.ribbonad} data-test-id="ribbonad">
          {presenter.ribbonad?.value || ''}
        </div>
        <ProductCardName
          className={styles['title']}
          index={index}
          product={product}
          currentItem={presenter.currentItem}
          storeConf={presenter.storeConf}
        />

        <ProductCardAttributes {...presenter.productAttributes} customClassName={styles.attributes} isCULayout={view === 'grid'} />

        <p className={catchPhraseStyle} ref={ref}>{presenter.productCatchPhrase}</p>

        {isCUItem ? (
          <MillesimaPrice
            className={{ container: styles.price, vat: styles.vat }}
            value={presenter.currentItem.offerPrice}
            vat={{
              display: true,
              included: isTtc
            }}
          />
        ) : (
          <ProductPrice
            customClass={styles['price']}
            customClassPrice={{ listPrice: styles['list-price'] }}
            displayUnitBloc={showUnitBloc}
            currentItem={presenter.currentItem}
            product={product}
            showVat={!isMobile}
            withFormat={isMobile}
            leftSide={view === 'grid'}
          />
        )}

        <div className={actionsStyle}>
          {!presenter.isMobile && !presenter.isAlert && (
            <>
              {(!isCategCu || presenter.isFilterCU) && (
                <Dropdown
                  id="item-selector"
                  customClassName={styles['item-selector']}
                  value={presenter.capacity?.value}
                  options={presenter.getDropdownOptions()}
                  onChange={(partnumber: string) => presenter.onItemChange(partnumber)}
                  isDisabled={presenter.productModel.itemsList.length === 0}
                  size={view === 'grid' ? 'small' : 'medium'}
                />
              )}

              <Dropdown
                customClassName={styles['quantity']}
                value={presenter.quantity}
                options={presenter.quantities}
                onChange={(value) => presenter.setQuantity(value)}
                size={view === 'grid' ? 'small' : 'medium'}
                isFullWidth={false}
              />
            </>
          )}

          <CallToActionContainer
            {...presenter.buttonProps}
            className={styles[presenter.buttonClassKey]}
            fullWidth
          >
            {presenter.buttonLabel}
          </CallToActionContainer>
        </div>

        <AvailabilityIndicator
          t={presenter.t}
          product={product}
          currentItem={presenter.currentItem}
          storeId={presenter.storeConf.storeId}
          locale={presenter.storeConf.locale}
          customClassName={styles['availability-indicator']}
          labelClassName={styles.label}
        />

        {view !== 'grid' && (
          <MillesimaLink
            id={`product${index}-goToProducer`}
            href={
              getHrefForCategLink(
                presenter.childrenProps.urlBrand, presenter.childrenProps.groupIdentifierBrand,
                presenter.childrenProps.isSpiritBrand
              )
            }
            as={formatUrl(presenter.childrenProps.urlBrand, presenter.childrenProps.isSpiritBrand, presenter.storeConf)}
            ariaLabel={presenter.childrenProps.brand}
            innerHtml={presenter.childrenProps.brand}
            size="small"
            customClassName={{ container: styles.producer }}
          />
        )}
      </div>
    </>
  );
};

export default ProductCard;
